import { useMutation } from '@tanstack/react-query'
import {
  autoSignIn,
  confirmSignUp as confirmSignUpAuth,
  fetchAuthSession,
  signUp as signUpAuth
} from 'aws-amplify/auth'
import { useFormik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { ConfirmCodeForm as Form, MapstackLoading, Seo } from 'ui-components'
import * as yup from 'yup'

import { useAtom } from 'user-auth/libs/jotai'
import { atoms } from 'user-auth/states/form'

const length = 6

const schema = yup.object().shape({
  code: yup.string().length(length).required()
})

export const ConfirmSignUp = render((props) => {
  const [email] = useAtom(atoms.otp.email)

  const [show, setShow] = useState<string[]>([])

  const { mutate: confirmSignUp } = useConfirmSignUpMutation({
    onSuccess: () => {
      setShow([])
    },
    onMutate: () => {
      const next = ['loading']
      setShow(next)
    },
    onError: () => {
      const next = ['error']
      setShow(next)
    }
  })

  const form = useFormik({
    validationSchema: schema,
    initialValues: {
      code: ''
    },
    validateOnMount: false,
    onSubmit: async (values) => {}
  })

  const handleChange = (value) => {
    form.setFieldValue('code', value)
  }

  const handleComplete = (value) => {
    confirmSignUp(value)
  }

  const handleClick = () => {}

  return (
    <Form
      render={show}
      email={email || 'name@company.com'}
      slotProps={{
        Form: {
          component: 'form',
          onSubmit: form.handleSubmit
        },
        OtpInput: {
          maxLength: length,
          value: form.values.code,
          onChange: handleChange,
          onComplete: handleComplete
        },
        AlertWarn: {
          onClick: handleClick
        },
        AlertError: {
          onClick: handleClick
        }
      }}
    />
  )
})

export function useSignUpOtpMutation() {
  return useMutation({
    mutationKey: ['signUpOtp'],
    mutationFn: async (variables: {
      email: string
      firstName: string
      lastName: string
    }) => {
      const { email, firstName, lastName } = variables

      await signUpAuth({
        username: email,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName
          },
          autoSignIn: {
            authFlowType: 'USER_AUTH',
            preferredChallenge: 'SMS_OTP'
          }
        }
      })
    }
  })
}

function useConfirmSignUpMutation(options?) {
  const [email] = useAtom(atoms.otp.email)

  const go = (pathname) => {
    window.location.href = pathname
  }

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  return useMutation({
    mutationKey: ['confirmSignUp'],
    mutationFn: async (code: string) => {
      const { nextStep, userId, isSignUpComplete } = await confirmSignUpAuth({
        username: email,
        confirmationCode: code
      })

      if (isSignUpComplete) {
        await autoSignIn()
        go('/sign-in/callback')
      }
    },
    onSuccess: options?.onSuccess || (() => {}),
    onMutate: options?.onMutate || (() => {}),
    onError: options?.onError || console.warn
  })
}

function render(Component) {
  return (props) => {
    const [email] = useAtom(atoms.otp.email)
    return (
      <>
        <Seo title='Confirm code' />
        {!email ? (
          <>
            <GotoSignUp />
            <MapstackLoading sx={{ height: '100vh' }} />
          </>
        ) : (
          <Component {...props} />
        )}
      </>
    )
  }
}

function GotoSignUp() {
  const router = useRouter()
  useEffect(() => {
    router.replace('/sign-up')
  }, [])
  return <></>
}
